import React from 'react';
import { Phone, ArrowRight, MapPin, Clock, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { businessInfo } from '../data/mock';

const Hero = () => {
  return (
    <section className="relative min-h-screen flex items-center pt-28 md:pt-32 pb-16 md:pb-20 overflow-hidden">
      {/* Background with subtle pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-amber-50">
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23f59e0b' fill-opacity='0.08'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 w-full">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          {/* Content */}
          <div className="order-2 lg:order-1">
            <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-3 md:px-4 py-2 rounded-full text-xs md:text-sm font-medium mb-4 md:mb-6 animate-pulse">
              <Clock className="w-4 h-4" />
              Free quotes - Usually respond within 24 hours
            </div>

            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-slate-800 leading-tight mb-4 md:mb-6">
              Professional Plastering
              <span className="block text-amber-500">Done Right</span>
            </h1>

            <p className="text-base md:text-xl text-slate-600 mb-6 md:mb-8 leading-relaxed max-w-xl">
              Smooth walls, clean finishes, reliable service. Quality work at fair prices across Greater Manchester.
            </p>

            {/* Feature badges - horizontal scroll on mobile */}
            <div className="flex gap-2 md:gap-3 mb-6 md:mb-8 overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0 md:flex-wrap">
              {['12-Month Guarantee', 'Price Promise', 'No VAT'].map((badge, index) => (
                <div key={index} className="flex items-center gap-2 bg-white px-3 md:px-4 py-2 rounded-lg shadow-sm border border-slate-100 whitespace-nowrap flex-shrink-0">
                  <CheckCircle2 className="w-4 md:w-5 h-4 md:h-5 text-green-500" />
                  <span className="text-slate-700 font-medium text-sm md:text-base">{badge}</span>
                </div>
              ))}
            </div>

            {/* CTA buttons */}
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 mb-6 md:mb-8">
              <Button 
                asChild
                size="lg"
                className="bg-amber-500 hover:bg-amber-600 text-white shadow-xl shadow-amber-200 transition-all hover:shadow-2xl hover:shadow-amber-300 text-base md:text-lg px-6 md:px-8 h-12 md:h-14"
              >
                <a href="#contact" className="flex items-center justify-center gap-2">
                  Get a Free Quote
                  <ArrowRight className="w-5 h-5" />
                </a>
              </Button>
              <Button 
                asChild
                variant="outline"
                size="lg"
                className="border-2 border-slate-300 hover:border-amber-500 hover:bg-amber-50 text-slate-700 text-base md:text-lg px-6 md:px-8 h-12 md:h-14 transition-all"
              >
                <a href={`tel:${businessInfo.phone}`} className="flex items-center justify-center gap-2">
                  <Phone className="w-5 h-5" />
                  {businessInfo.phone}
                </a>
              </Button>
            </div>

            {/* Coverage area */}
            <div className="flex items-center gap-2 text-slate-500 text-sm md:text-base">
              <MapPin className="w-5 h-5 text-amber-500 flex-shrink-0" />
              <span>Covering <strong className="text-slate-700">Greater Manchester</strong> & Surrounding Areas</span>
            </div>
          </div>

          {/* Image - hidden on mobile for faster load, shown on tablet+ */}
          <div className="order-1 lg:order-2 relative hidden md:block">
            <div className="relative">
              {/* Main image */}
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.pexels.com/photos/10383588/pexels-photo-10383588.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop" 
                  alt="Professional plasterer at work"
                  className="w-full h-[400px] md:h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent" />
              </div>

              {/* Floating card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-5 border border-slate-100 max-w-[280px]">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-800">Trusted Local Plasterer</p>
                    <p className="text-sm text-slate-500">15+ Years Experience</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-amber-400 fill-current" viewBox="0 0 20 20">
                      <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
                    </svg>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
